import { ChangeEvent, Dispatch, FunctionComponent, ReactNode } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import BillingAddressInformation from '@/components/PaymentDetails/BillingAddressInformation'
import TipSelect from '@/components/TipSelect'
import WePayIframe from '@/components/WePayIframe'
import { VenueBranding } from '@/libs/helpers/adapters'
import {
  CardCountry,
  GiftCard,
  GiftCardRequest,
  PaymentMethod,
  StreetAddressState,
  TBPaymentType,
} from '@/libs/helpers/apiClient'
import { OrderMethod } from '@/libs/helpers/utils'
import { validatePostalCode, validateZipCode } from '@/libs/helpers/validators'
import { OrderingAddress } from '@/providers/googleMaps'
import { Divider, formStyles, Grid, Select, SelectVariant, SvgIcon, Text, TextField } from '@/tbui'
import PayrixIframe from 'src/components/PayrixIframe'
import GiftCardPicker from './GiftCardPicker'
import SupportedCardBrands, { CardBrands } from './SupportedCardBrands'

const StyledGrid = styled(Grid)`
  @media (min-width: ${(props) => props.theme.breakpoints.SM}) {
    max-width: 670px;
  }
`

const StyledWrapper = styled(Grid)`
  height: 30vh;
  justify-items: center;
  @media (min-width: ${(props) => props.theme.breakpoints.SM}) {
    max-width: 670px;
  }
`

interface OnlinePaymentProps {
  setCardCountry: Dispatch<CardCountry>
  setPostalOrZipCode: Dispatch<string>
  setIsValidPostal: Dispatch<boolean>
  setWePay: Dispatch<WePayIframe>
  wePayIframe: WePayIframe | null
  setTipAmount: Dispatch<string>
  paymentMethod: PaymentMethod
  cardCountry: CardCountry
  postalOrZipCode: string
  isValidPostal: boolean
  disableTip: boolean
  isWePaySDKLoaded: boolean
  hasWePayError: boolean
  setHasWePayError: Dispatch<boolean>
  handleDGCChange: (e: ChangeEvent<HTMLInputElement>) => void
  giftCardOptions: GiftCard[]
  isGiftCardPicked: boolean
  isDGCEnabled: boolean
  selectedGiftCardCode?: string | null
  applyGiftCardError: string | null
  handleAddGiftCard: ({ code, recaptchaToken }: GiftCardRequest) => void
  showOnlinePayment: boolean
  isCartLoading: boolean
  isEmptyCart: boolean
  totalAfterTax: string
  tbPaymentType: TBPaymentType | null
  streetAddress: StreetAddressState | null
  setStreetAddress: (value: StreetAddressState | null) => void
  zipCode: string
  setZipCode: (value: string) => void
  validBillingAddress: boolean
  setValidZipCode: (value: boolean) => void
  setValidBillingAddress: (value: boolean) => void
  deliveryAddress?: OrderingAddress
  orderMethod: OrderMethod
  payrixSDKLoaded: boolean
  venueBranding?: VenueBranding
}

const OnlinePayment: FunctionComponent<OnlinePaymentProps> = ({
  setPostalOrZipCode,
  setIsValidPostal,
  postalOrZipCode,
  setCardCountry,
  paymentMethod,
  isValidPostal,
  setTipAmount,
  cardCountry,
  setWePay,
  wePayIframe,
  disableTip,
  isWePaySDKLoaded,
  hasWePayError,
  setHasWePayError,
  handleDGCChange,
  giftCardOptions,
  isGiftCardPicked,
  isDGCEnabled,
  selectedGiftCardCode,
  applyGiftCardError,
  handleAddGiftCard,
  showOnlinePayment,
  isCartLoading,
  isEmptyCart,
  totalAfterTax,
  tbPaymentType,
  streetAddress,
  setStreetAddress,
  zipCode,
  setZipCode,
  validBillingAddress,
  setValidZipCode,
  setValidBillingAddress,
  deliveryAddress,
  orderMethod,
  payrixSDKLoaded,
  venueBranding,
}) => {
  // INITIALIZE HOOKS
  const { t } = useTranslation()
  const theme = useTheme()

  const isDelivery = orderMethod === OrderMethod.delivery
  const isCanadianCard = cardCountry === CardCountry.CA
  const shouldDisplayBillingAddress =
    tbPaymentType === TBPaymentType.PAYRIX || tbPaymentType === TBPaymentType.WORLDPAY
  const isWePayPayment = tbPaymentType === TBPaymentType.WEPAY
  const isPayrixPayment = tbPaymentType === TBPaymentType.PAYRIX

  if (isEmptyCart) {
    return null
  }

  // transforms enum keys to array of values
  const cardCountryOptions = Object.values(CardCountry).map((cCountry: CardCountry) => ({
    key: cCountry,
    label: t('checkout.payment.cc_country', {
      // normalize to lowercase
      context: cCountry.toLowerCase(),
    }),
    value: cCountry,
  }))

  const handleTipChange = (tipAmount: string): void => {
    setTipAmount(tipAmount)
  }

  const handleCountryChange = (cCountry: string): void => {
    setCardCountry(cCountry as CardCountry)
    // hide error message if any
    setIsValidPostal(true)
  }

  const handlePostalOrZipCodeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPostalOrZipCode(e.target.value == null ? '' : e.target.value.replace(/\s+/g, '')) // trim all whitespace
  }

  const handlePostalOrZipCodeBlur = (): void => {
    const isValidPostalOrZip = isCanadianCard
      ? validatePostalCode(postalOrZipCode)
      : validateZipCode(postalOrZipCode)
    setIsValidPostal(isValidPostalOrZip)
  }

  if (hasWePayError) {
    return (
      <StyledWrapper
        className="wrapper-payment"
        gap="8px"
        align="center"
        data-test="online-payment-unavailable"
      >
        <SvgIcon fontSize="50px" name="alert" color="ERROR" />
        <Text type="H2" align="center">
          {t('checkout.payment.online_payment_unavailable_title')}
        </Text>
        <Text align="center">{t('checkout.payment.online_payment_unavailable_message')}</Text>
      </StyledWrapper>
    )
  }

  const renderOnlinePaymentDetails = (): ReactNode => {
    if (showOnlinePayment) {
      return (
        <>
          {isWePayPayment && (
            <>
              {isDGCEnabled && <Text type="H4">{t('checkout.payment.pay_gift_credit_debit')}</Text>}
              <WePayIframe
                paymentMethod={paymentMethod}
                isWePaySDKLoaded={isWePaySDKLoaded}
                setWePay={setWePay}
                setHasWePayError={setHasWePayError}
                hasWePayError={hasWePayError}
              />
              {wePayIframe != null ? (
                <>
                  <StyledGrid m="-18px 0 30px 0" data-test="cc-country">
                    <Text fontSize={formStyles(theme).labelFontSize} m="0 0 8px 0">
                      {t('checkout.payment.cc_country_label')}
                    </Text>
                    <Select
                      variant={SelectVariant.INPUT}
                      onChange={handleCountryChange}
                      options={cardCountryOptions}
                      currentLabel={
                        <Text color="TEXT" fontSize="14px" data-test="cc-country-selected">
                          {t('checkout.payment.cc_country', {
                            context: cardCountry.toLowerCase(),
                          })}
                        </Text>
                      }
                    />
                  </StyledGrid>
                  <StyledGrid data-test="postal-code">
                    <label
                      htmlFor="postal-code"
                      css={[formStyles(theme).label, !isValidPostal && formStyles(theme).invalid]}
                    >
                      {isCanadianCard
                        ? t('checkout.payment.cc_postalcode')
                        : t('checkout.payment.cc_zipcode')}
                      <TextField
                        name={isCanadianCard ? 'cc-postalcode' : 'cc-zipcode'}
                        id="postal-code"
                        value={postalOrZipCode}
                        css={formStyles(theme).input}
                        variant={SelectVariant.INPUT}
                        // hide error message when field is focused
                        onFocus={(): void => setIsValidPostal(true)}
                        onChange={handlePostalOrZipCodeChange}
                        onBlur={handlePostalOrZipCodeBlur}
                      />
                    </label>
                    {!isValidPostal && (
                      <Text color="ERROR" m="10px 0 0 0">
                        {isCanadianCard
                          ? t('checkout.payment.invalid_cc_postalcode')
                          : t('checkout.payment.invalid_cc_zipcode')}
                      </Text>
                    )}
                  </StyledGrid>
                </>
              ) : null}
            </>
          )}

          {isPayrixPayment && (
            <>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Text type="H4">{t('checkout.payment.pay_gift_credit_debit')}</Text>
                <SupportedCardBrands
                  theme={theme}
                  brands={[CardBrands.VISA, CardBrands.MASTERCARD].concat(
                    cardCountry === CardCountry.CA ? [] : [CardBrands.AMEX, CardBrands.DISCOVER]
                  )}
                />
              </div>
              <PayrixIframe payrixSDKLoaded={payrixSDKLoaded} venueBranding={venueBranding} />
            </>
          )}

          {shouldDisplayBillingAddress && (
            <BillingAddressInformation
              deliveryAddress={deliveryAddress}
              streetAddress={streetAddress}
              setStreetAddress={setStreetAddress}
              payrixZipCode={zipCode}
              setPayrixZipCode={setZipCode}
              validBillingAddress={validBillingAddress}
              setValidZipCode={setValidZipCode}
              setValidBillingAddress={setValidBillingAddress}
              orderMethod={orderMethod}
            />
          )}
        </>
      )
    }
  }

  return (
    <>
      {!disableTip ? (
        <>
          <Text type="H4" m="24px 0">
            {t('checkout.payment.add_tip', {
              context: isDelivery,
            })}
          </Text>
          <TipSelect
            onChange={handleTipChange}
            totalAfterTax={totalAfterTax}
            isCartLoading={isCartLoading}
          />
        </>
      ) : null}
      {isDGCEnabled && (
        <GiftCardPicker
          title={t('checkout.payment.pay_gift_card_online')}
          isCartLoading={isCartLoading}
          handleDGCChange={handleDGCChange}
          giftCardOptions={giftCardOptions}
          isGiftCardPicked={isGiftCardPicked}
          selectedGiftCardCode={selectedGiftCardCode}
          handleAddGiftCard={handleAddGiftCard}
          applyGiftCardError={applyGiftCardError}
        />
      )}
      <Grid m="20px 0">
        <Divider />
      </Grid>
      {renderOnlinePaymentDetails()}
    </>
  )
}

export default OnlinePayment
